/* eslint-disable max-lines */
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import UserIcon from '@westwing/ui-kit/Icons/UserIcon';
import CartIcon from '@westwing/ui-kit/Icons/CartIcon';
import {
    MenuSubLabel,
    MenuLabel,
    Icons,
    ItemWrapper,
    DesktopOnlyItemWrapper,
    IconItemWrapper,
    IconLink,
    MenuIconWrapper,
} from './IconsButtonTray.styles';
import CountBubble from './CountBubble';
import AccountContent from './AccountContent';
import CartOverlay from './CartOverlay';
import { WishlistMenuItem } from './WishlistMenuItem';
import { MoodBoardMenuItem } from './MoodBoardMenuItem';
import useTranslator from 'Client/hooks/useTranslator';
import StateInterface from 'Client/redux/types';
import { OverlayId } from 'Client/components/Overlays/types';
import { useOverlayStateChecker } from 'Client/components/Overlays/useOverlayStateChecker';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import useConfig from 'Client/hooks/useConfig';
import useGa from 'Client/hooks/useGa';
import { usePageResolver } from 'Client/components/ListingPage/hooks/useTrackingPageLabel';
import { PageType } from 'Client/hooks/UrlManager/types';
import { TrackHeaderActions } from 'Common/ga/types';
import { CartFlyoutTimeoutKey } from 'Common/appshell_types/customEventTypes';
import { VictorQaClasses, BellaTestIds } from 'Common/qaIds';
import { CartProductItem } from 'Client/redux/cart/types';
import { fetchCartData } from 'Client/redux/cart/actions';
import { setRedirectUrl } from 'Client/redux/user/actions';
import { hideOverlay, showOverlay } from 'Client/redux/header/overlays/actions';
import { remoteLoginEvent } from 'Common/events/service';
import { SessionTypes } from 'Common/appshell_types';
import { useBellaSelector } from 'Client/redux/hooks';
import useLoginAndRegisterOverlayWithTracking from 'Client/hooks/useLoginAndRegisterOverlayWithTracking';
import ContentOverlay from 'Client/components/Overlays/ContentOverlay';

const MAX_USER_NAME_LENGTH = 10;

export const getUserDisplayName = (firstName = '', email = '', isUserLoggedIn: boolean) => {
    const defaultName = firstName || email || '';
    const accountName = isUserLoggedIn ? defaultName : '';

    return accountName.length > MAX_USER_NAME_LENGTH ? `${accountName.substring(0, 7)}...` : accountName;
};

interface Props {
    cartCount: number;
    wishListItemCount: number;
    wishListUrl: string;
    myAccountUrl: string;
    cartUrl: string;
    carts: CartProductItem[];
    subtotal?: string;
}

const cartPopupDisabledPageTypes = [PageType.CART, PageType.ALICE_CART];

const IconsButtonTray = ({
    wishListItemCount,
    cartCount,
    wishListUrl,
    cartUrl,
    myAccountUrl,
    subtotal = '',
    carts,
}: Props) => {
    const t = useTranslator();
    const dispatch = useDispatch();
    const bellaPush = useBellaPush();
    const config = useConfig();
    const { countryCode, currency } = config;

    const { data, isLoggedIn } = useBellaSelector(state => state.appShellLogin);
    const isCartDataFetched = useBellaSelector(state => state.header.cart.isCartDataFetched);
    const isUserFullyLoggedIn = isLoggedIn && data?.sessionType === SessionTypes.full;
    const isPartiallyLoggedIn = isLoggedIn && data?.sessionType === SessionTypes.partial;
    const firstName = data?.firstName;
    const email = data?.email;

    const accountName = getUserDisplayName(firstName, email, isLoggedIn);
    const ga = useGa();
    const pageTypeLabel = usePageResolver();
    const overlayState = useOverlayStateChecker();
    const {
        showLoginAndRegisterOverlay,
        trackLoginOverlayShow,
        trackRegistrationOverlayShow,
    } = useLoginAndRegisterOverlayWithTracking();
    const isOverlayActive = (id: OverlayId): boolean => {
        const overlay = overlayState(id);
        return overlay && overlay.isActive;
    };

    const remoteLogin = remoteLoginEvent();

    const handleAccountClick = () => {
        ga.oneHeader.trackHeaderAction('accounticonclick', TrackHeaderActions.HeaderMyAccountClick);
        if (isUserFullyLoggedIn && isDesktop()) {
            bellaPush(myAccountUrl);
            return;
        }
        dispatch(setRedirectUrl(myAccountUrl));
        showLoginAndRegisterOverlay({ isLogin: isPartiallyLoggedIn });
        trackLoginOverlayShow({ isLogin: isPartiallyLoggedIn, origin: 'accountBtn' });
        if (!isLoggedIn) {
            trackRegistrationOverlayShow('accountBtn');
        }
        remoteLogin({ redirectUrl: myAccountUrl, email, isLogin: isPartiallyLoggedIn });
    };

    const handleCartClick = () => {
        ga.oneHeader.trackHeaderAction('carticonclick', TrackHeaderActions.HeaderCartClick);
    };

    const shouldFetchCartFlyoutData = cartCount > 0 && !cartPopupDisabledPageTypes.includes(pageTypeLabel);

    const handleItemHover = (overlayId: OverlayId) => {
        if (overlayId === OverlayId.DESKTOP_CART_OVERLAY) {
            window.clearTimeout(window[CartFlyoutTimeoutKey]);

            if (shouldFetchCartFlyoutData) {
                // Fetch the cart data only when it is not present in the store.
                if (!isCartDataFetched) {
                    dispatch(fetchCartData({ countryCode, currency }));
                }
                dispatch(showOverlay(overlayId));
            }
            return;
        }

        if (isLoggedIn) {
            dispatch(showOverlay(overlayId));
        }

        if (isOverlayActive(OverlayId.DESKTOP_NAV_FLYOUT)) {
            dispatch(hideOverlay(OverlayId.DESKTOP_NAV_FLYOUT));
        }
    };

    const closeActiveOverlay = (id: OverlayId) => {
        if (isOverlayActive(id)) {
            dispatch(hideOverlay(id));
        }
    };
    const accountIconClassname = isLoggedIn ? VictorQaClasses.headerAccountLink : VictorQaClasses.headerSignUpLink;
    return (
        <Icons>
            <DesktopOnlyItemWrapper
                onMouseEnter={() => handleItemHover(OverlayId.DESKTOP_ACCOUNT_OVERLAY)}
                onMouseLeave={() => closeActiveOverlay(OverlayId.DESKTOP_ACCOUNT_OVERLAY)}
                data-testid="one-header-icon-my-account"
            >
                <IconItemWrapper className={accountIconClassname} onClick={handleAccountClick}>
                    <MenuIconWrapper>
                        <UserIcon />
                    </MenuIconWrapper>
                    <MenuLabel>{t('My account')}</MenuLabel>
                    <MenuSubLabel>{accountName}</MenuSubLabel>
                </IconItemWrapper>
                <ContentOverlay identifier={OverlayId.DESKTOP_ACCOUNT_OVERLAY} rightPosition={4} propagateOutsideClicks>
                    <AccountContent />
                </ContentOverlay>
            </DesktopOnlyItemWrapper>
            <MoodBoardMenuItem />
            <WishlistMenuItem
                isLoggedIn={isLoggedIn}
                isPartiallyLoggedIn={isPartiallyLoggedIn}
                wishListItemCount={wishListItemCount}
                wishListUrl={wishListUrl}
                email={email}
            />
            <ItemWrapper
                onMouseLeave={() => closeActiveOverlay(OverlayId.DESKTOP_CART_OVERLAY)}
                onMouseEnter={() => handleItemHover(OverlayId.DESKTOP_CART_OVERLAY)}
                onClick={handleCartClick}
                data-testid="one-header-icon-cart"
            >
                <IconLink to={cartUrl} className={VictorQaClasses.headerCartLink}>
                    <MenuIconWrapper>
                        <CartIcon />
                        {cartCount > 0 && (
                            <CountBubble
                                count={cartCount}
                                counterTestId={BellaTestIds.headerCartCounter}
                                className={VictorQaClasses.headerCartCounter}
                            />
                        )}
                    </MenuIconWrapper>
                    <MenuLabel>{t('Cart')}</MenuLabel>
                </IconLink>
                <CartOverlay carts={carts} subtotal={subtotal} cartUrl={cartUrl} cartCount={cartCount} />
            </ItemWrapper>
        </Icons>
    );
};

const mapStateToProps = (state: StateInterface) => {
    const { count: cartCount, carts, subtotal } = state.header.cart;
    return { cartCount, carts, subtotal };
};

export default connect(mapStateToProps)(IconsButtonTray);
